'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function FuelCostCalculator() {
  const t = useTranslations("tools.fuelCostCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [inputs, setInputs] = useState({
    tripDistance: 500,
    distanceUnit: 'miles',
    fuelEfficiency: 25,
    efficiencyUnit: 'mpg',
    fuelPrice: 3.50,
    priceUnit: 'gallon'
  });

  const [results, setResults] = useState<any>(null);
  const [error, setError] = useState('');

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const convertToMiles = (distance: number, unit: string) => {
    if (unit === 'kilometers') {
      return distance * 0.621371;
    }
    return distance;
  };

  const convertToGallons = (efficiency: number, efficiencyUnit: string, distance: number, distanceUnit: string) => {
    let distanceInMiles = convertToMiles(distance, distanceUnit);

    switch(efficiencyUnit) {
      case 'mpg':
        return distanceInMiles / efficiency;
      case 'lp100k':
        let distanceInKm = distanceUnit === 'miles' ? distance * 1.60934 : distance;
        return (distanceInKm / 100 * efficiency) / 3.78541;
      case 'kpl':
        let distanceInKm2 = distanceUnit === 'miles' ? distance * 1.60934 : distance;
        return (distanceInKm2 / efficiency) / 3.78541;
      case 'lpm':
        return distanceInMiles * efficiency / 3.78541;
      default:
        return 0;
    }
  };

  const handleCalculate = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const { tripDistance, distanceUnit, fuelEfficiency, efficiencyUnit, fuelPrice, priceUnit } = inputs;

    if (tripDistance <= 0 || fuelEfficiency <= 0 || fuelPrice <= 0) {
      setError(t('errorInvalidNumbers'));
      setResults(null);
      return;
    }

    let gallonsNeeded = convertToGallons(fuelEfficiency, efficiencyUnit, tripDistance, distanceUnit);
    let pricePerGallon = priceUnit === 'liter' ? fuelPrice * 3.78541 : fuelPrice;
    let totalCost = gallonsNeeded * pricePerGallon;
    let costPerUnit = totalCost / tripDistance;

    let fuelUnit = priceUnit === 'liter' ? 'liters' : 'gallons';
    let fuelAmount = priceUnit === 'liter' ? gallonsNeeded * 3.78541 : gallonsNeeded;

    let efficiencyText = fuelEfficiency.toFixed(2) + ' ';
    switch(efficiencyUnit) {
      case 'mpg': efficiencyText += 'mpg'; break;
      case 'lp100k': efficiencyText += 'L/100km'; break;
      case 'kpl': efficiencyText += 'km/L'; break;
      case 'lpm': efficiencyText += 'L/mile'; break;
    }

    setResults({
      totalCost: totalCost.toFixed(2),
      fuelNeeded: fuelAmount.toFixed(2),
      fuelUnit,
      costPerUnit: costPerUnit.toFixed(3),
      distanceUnit: distanceUnit === 'miles' ? 'mile' : 'km',
      detailDistance: tripDistance.toFixed(2) + ' ' + distanceUnit,
      detailEfficiency: efficiencyText,
      detailPrice: '$' + fuelPrice.toFixed(2) + ' per ' + (priceUnit === 'liter' ? 'liter' : 'gallon'),
      detailConsumption: fuelAmount.toFixed(2) + ' ' + fuelUnit
    });
  };

  const handleInputChange = (field: string, value: any) => {
    setInputs(prev => ({ ...prev, [field]: value }));
    if (results) {
      setTimeout(() => {
        const form = document.querySelector('form');
        if (form) {
          const event = new Event('submit', { bubbles: true, cancelable: true });
          form.dispatchEvent(event);
        }
      }, 100);
    }
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    { fontWeight: 700, color: colors.textPrimary, marginBottom: '1rem', lineHeight: 1.2 },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6, margin: '0 auto' },
    { fontSize: '1rem', maxWidth: '100%' },
    { fontSize: '1.125rem', maxWidth: '700px' },
    { fontSize: '1.25rem', maxWidth: '800px' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #f0f9ff 0%, #e0f2fe 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
      boxShadow: '0 4px 20px rgba(37, 99, 235, 0.1)'
    },
    { padding: '1.5rem 1rem' },
    { padding: '2rem 1.5rem', borderRadius: '1.25rem' },
    { padding: '3rem 1.5rem', borderRadius: '1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { maxWidth: '900px', margin: '0 auto', background: 'white', borderRadius: '1rem', overflow: 'hidden', boxShadow: '0 10px 40px rgba(0, 0, 0, 0.1)' },
    {},
    {},
    {}
  );

  const toolHeaderStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white', textAlign: 'center' as const },
    { padding: '1.5rem' },
    { padding: '1.75rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700, color: 'white' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const calculatorFormStyle = getResponsiveStyle(
    {},
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const formGroupStyle = { marginBottom: '1.5rem' };

  const labelStyle = getResponsiveStyle(
    { display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#1e293b' },
    { fontSize: '0.9rem' },
    { fontSize: '0.95rem' },
    { fontSize: '1rem' }
  );

  const inputWrapperStyle = getResponsiveStyle(
    { display: 'flex', gap: '0.5rem', alignItems: 'stretch', flexDirection: 'column' as const },
    {},
    { flexDirection: 'row' as const },
    { flexDirection: 'row' as const }
  );

  const inputStyle = getResponsiveStyle(
    {
      flex: 1,
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      background: 'white'
    },
    { padding: '0.75rem 1rem', fontSize: '1rem' },
    { padding: '0.875rem 1.125rem', fontSize: '1.0625rem' },
    { padding: '1rem 1.25rem', fontSize: '1.125rem' }
  );

  const selectStyle = getResponsiveStyle(
    {
      cursor: 'pointer',
      border: '2px solid #e2e8f0',
      borderRadius: '0.5rem',
      transition: 'all 0.3s ease',
      background: 'white'
    },
    { padding: '0.75rem 1rem', fontSize: '1rem', minWidth: '100%' },
    { padding: '0.875rem 1.125rem', fontSize: '1.0625rem', minWidth: '180px' },
    { padding: '1rem 1.25rem', fontSize: '1.125rem', minWidth: '200px' }
  );

  const calculateBtnStyle = getResponsiveStyle(
    {
      width: '100%',
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      border: 'none',
      borderRadius: '0.5rem',
      fontWeight: 600,
      cursor: 'pointer',
      transition: 'all 0.3s ease',
      marginTop: '1rem'
    },
    { padding: '0.875rem 1.5rem', fontSize: '1rem' },
    { padding: '1rem 2rem', fontSize: '1.0625rem' },
    { padding: '1.125rem 2rem', fontSize: '1.125rem' }
  );

  const errorMessageStyle = {
    background: '#fef2f2',
    color: '#dc2626',
    padding: '1rem',
    borderRadius: '0.5rem',
    marginBottom: '1rem',
    borderLeft: '4px solid #dc2626',
    fontSize: '0.95rem'
  };

  const resultsStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)', borderTop: '3px solid #e2e8f0' },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const resultsH3Style = getResponsiveStyle(
    { margin: '0 0 1.5rem 0', color: '#1e293b', textAlign: 'center' as const },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const resultGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginBottom: '2rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(3, 1fr)' }
  );

  const resultCardStyle = getResponsiveStyle(
    { background: 'white', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)', borderLeft: '4px solid #2563eb' },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const resultCardHighlightStyle = getResponsiveStyle(
    { background: 'linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%)', borderRadius: '0.75rem', boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)', borderLeft: '4px solid #f59e0b' },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const resultLabelStyle = { display: 'block', fontSize: '0.85rem', color: '#64748b', marginBottom: '0.5rem', fontWeight: 500, textTransform: 'uppercase' as const, letterSpacing: '0.5px' };

  const resultValueStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 700, color: '#1e293b' },
    { fontSize: '1.5rem' },
    { fontSize: '1.625rem' },
    { fontSize: '1.75rem' }
  );

  const resultValueHighlightStyle = getResponsiveStyle(
    { display: 'block', fontWeight: 700, color: '#f59e0b' },
    { fontSize: '1.5rem' },
    { fontSize: '1.625rem' },
    { fontSize: '1.75rem' }
  );

  const breakdownSectionStyle = getResponsiveStyle(
    { background: 'white', borderRadius: '0.75rem', marginTop: '1.5rem' },
    { padding: '1.25rem' },
    { padding: '1.375rem' },
    { padding: '1.5rem' }
  );

  const breakdownH4Style = { margin: '0 0 1rem 0', color: '#1e293b', fontSize: '1.1rem' };

  const breakdownItemStyle = { display: 'flex', justifyContent: 'space-between', padding: '0.75rem 0', borderBottom: '1px solid #e2e8f0' };

  const breakdownLabelStyle = { color: '#64748b', fontWeight: 500 };
  const breakdownValueStyle = { color: '#1e293b', fontWeight: 600 };

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem'
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem', marginTop: 0 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = getResponsiveStyle(
    { lineHeight: 1.8, color: colors.textSecondary, paddingLeft: '1.5rem', marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const listItemStyle = { marginBottom: '0.75rem' };

  const strongStyle = { fontWeight: 600, color: colors.textPrimary };

  const faqItemStyle = getResponsiveStyle(
    { marginBottom: '1.5rem', paddingBottom: '1.5rem', borderBottom: '1px solid rgba(255, 200, 180, 0.2)' },
    {},
    {},
    {}
  );

  const faqQuestionStyle = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.5rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.1875rem' }
  );

  const faqAnswerStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const referenceStyle = getResponsiveStyle(
    { marginBottom: '0.75rem', paddingLeft: '1.5rem', position: 'relative' as const },
    { fontSize: '0.9375rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' }
  );

  const referenceLinkStyle = { color: colors.primary, textDecoration: 'none', transition: 'color 0.3s ease' };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t('title')}</h1>
        <p style={summaryTextStyle}>{t('subtitle')}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>⛽ {t('toolTitle')}</h2>
          </div>

          <form style={calculatorFormStyle} onSubmit={handleCalculate}>
            {error && (
              <div style={errorMessageStyle}>
                {error}
              </div>
            )}

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="trip-distance">{t('tripDistanceLabel')}</label>
              <div style={inputWrapperStyle}>
                <input
                  type="number"
                  id="trip-distance"
                  step="0.01"
                  min="0"
                  value={inputs.tripDistance}
                  onChange={(e) => handleInputChange('tripDistance', parseFloat(e.target.value) || 0)}
                  required
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
                <select
                  id="distance-unit"
                  value={inputs.distanceUnit}
                  onChange={(e) => handleInputChange('distanceUnit', e.target.value)}
                  style={selectStyle}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                >
                  <option value="miles">{t('unitMiles')}</option>
                  <option value="kilometers">{t('unitKilometers')}</option>
                </select>
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="fuel-efficiency">{t('fuelEfficiencyLabel')}</label>
              <div style={inputWrapperStyle}>
                <input
                  type="number"
                  id="fuel-efficiency"
                  step="0.01"
                  min="0.01"
                  value={inputs.fuelEfficiency}
                  onChange={(e) => handleInputChange('fuelEfficiency', parseFloat(e.target.value) || 0)}
                  required
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
                <select
                  id="efficiency-unit"
                  value={inputs.efficiencyUnit}
                  onChange={(e) => handleInputChange('efficiencyUnit', e.target.value)}
                  style={selectStyle}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                >
                  <option value="mpg">{t('unitMpg')}</option>
                  <option value="lp100k">{t('unitLp100k')}</option>
                  <option value="kpl">{t('unitKpl')}</option>
                  <option value="lpm">{t('unitLpm')}</option>
                </select>
              </div>
            </div>

            <div style={formGroupStyle}>
              <label style={labelStyle} htmlFor="fuel-price">{t('fuelPriceLabel')}</label>
              <div style={inputWrapperStyle}>
                <input
                  type="number"
                  id="fuel-price"
                  step="0.01"
                  min="0.01"
                  value={inputs.fuelPrice}
                  onChange={(e) => handleInputChange('fuelPrice', parseFloat(e.target.value) || 0)}
                  required
                  style={inputStyle}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                />
                <select
                  id="price-unit"
                  value={inputs.priceUnit}
                  onChange={(e) => handleInputChange('priceUnit', e.target.value)}
                  style={selectStyle}
                  onFocus={(e) => e.target.style.borderColor = '#2563eb'}
                  onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                >
                  <option value="gallon">{t('unitPerGallon')}</option>
                  <option value="liter">{t('unitPerLiter')}</option>
                </select>
              </div>
            </div>

            <button
              type="submit"
              style={calculateBtnStyle}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = 'translateY(-2px)';
                e.currentTarget.style.boxShadow = '0 10px 25px rgba(37, 99, 235, 0.3)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = 'translateY(0)';
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              {t('calculateButton')}
            </button>
          </form>

          {results && (
            <div style={resultsStyle}>
              <h3 style={resultsH3Style}>💰 {t('resultsTitle')}</h3>

              <div style={resultGridStyle}>
                <div style={resultCardHighlightStyle}>
                  <span style={resultLabelStyle}>{t('totalFuelCostLabel')}</span>
                  <span style={resultValueHighlightStyle}>${results.totalCost}</span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('fuelNeededLabel')}</span>
                  <span style={resultValueStyle}>{results.fuelNeeded} {results.fuelUnit}</span>
                </div>

                <div style={resultCardStyle}>
                  <span style={resultLabelStyle}>{t('costPerUnitLabel')}</span>
                  <span style={resultValueStyle}>${results.costPerUnit} {t('per')} {results.distanceUnit}</span>
                </div>
              </div>

              <div style={breakdownSectionStyle}>
                <h4 style={breakdownH4Style}>📊 {t('tripDetailsTitle')}</h4>
                <div style={breakdownItemStyle}>
                  <span style={breakdownLabelStyle}>{t('tripDistanceLabel')}</span>
                  <span style={breakdownValueStyle}>{results.detailDistance}</span>
                </div>
                <div style={breakdownItemStyle}>
                  <span style={breakdownLabelStyle}>{t('fuelEfficiencyLabel')}</span>
                  <span style={breakdownValueStyle}>{results.detailEfficiency}</span>
                </div>
                <div style={breakdownItemStyle}>
                  <span style={breakdownLabelStyle}>{t('fuelPriceLabel')}</span>
                  <span style={breakdownValueStyle}>{results.detailPrice}</span>
                </div>
                <div style={{ ...breakdownItemStyle, borderBottom: 'none' }}>
                  <span style={breakdownLabelStyle}>{t('estimatedConsumptionLabel')}</span>
                  <span style={breakdownValueStyle}>{results.detailConsumption}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('introTitle')}</h2>
        <p style={paragraphStyle}>{t('introParagraph1')}</p>
        <p style={paragraphStyle}>{t('introParagraph2')}</p>
        <p style={paragraphStyle}>{t('introParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('howToUseTitle')}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step1Title')}</strong> {t('step1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step2Title')}</strong> {t('step2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step3Title')}</strong> {t('step3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step4Title')}</strong> {t('step4Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('step5Title')}</strong> {t('step5Description')}</li>
        </ol>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('insightsTitle')}</h2>
        <p style={paragraphStyle}>{t('insightsParagraph1')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight1Title')}</strong> {t('insight1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight2Title')}</strong> {t('insight2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight3Title')}</strong> {t('insight3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight4Title')}</strong> {t('insight4Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('insight5Title')}</strong> {t('insight5Description')}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('detailsTitle')}</h2>
        <p style={paragraphStyle}>{t('detailsParagraph1')}</p>
        <p style={paragraphStyle}>{t('detailsParagraph2')}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detail1Title')}</strong> {t('detail1Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detail2Title')}</strong> {t('detail2Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detail3Title')}</strong> {t('detail3Description')}</li>
          <li style={listItemStyle}><strong style={strongStyle}>{t('detail4Title')}</strong> {t('detail4Description')}</li>
        </ul>
        <p style={paragraphStyle}>{t('detailsParagraph3')}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('faqTitle')}</h2>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq1Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq1Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq2Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq2Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq3Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq3Answer')}</p>
        </div>

        <div style={faqItemStyle}>
          <h3 style={faqQuestionStyle}>{t('faq4Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq4Answer')}</p>
        </div>

        <div style={{ ...faqItemStyle, borderBottom: 'none' }}>
          <h3 style={faqQuestionStyle}>{t('faq5Question')}</h3>
          <p style={faqAnswerStyle}>{t('faq5Answer')}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t('referencesTitle')}</h2>
        <div style={referenceStyle}>
          <a
            href="https://myfuelcalc.com"
            target="_blank"
            rel="noopener noreferrer"
            style={referenceLinkStyle}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('reference1')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a
            href="https://outgrow.co/templates/fuel-cost-calculator/"
            target="_blank"
            rel="noopener noreferrer"
            style={referenceLinkStyle}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('reference2')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a
            href="https://www.fuellogic.net/benefits-of-using-a-fuel-estimator-for-your-fleet-operations/"
            target="_blank"
            rel="noopener noreferrer"
            style={referenceLinkStyle}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('reference3')}
          </a>
        </div>
        <div style={referenceStyle}>
          <a
            href="https://www.calculator.net/fuel-cost-calculator.html"
            target="_blank"
            rel="noopener noreferrer"
            style={referenceLinkStyle}
            onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
            onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
          >
            {t('reference4')}
          </a>
        </div>
      </section>
    </div>
  );
}